import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from './ToastProvider';
import { hasPermission as checkPermission, canAccessAdmin } from '../utils/permissions';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [email, setemail] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleId, setRoleId] = useState('customer');
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token') || sessionStorage.getItem('token') || "");
  const navigate = useNavigate();
  const location = useLocation();
  const apiAuthUrl = `${process.env.REACT_APP_API_URL}/api/auth` 
  const [profile, setProfile] = useState(null);
  const { showToast } = useToast();

  const saveToken = (newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
    sessionStorage.setItem('token', newToken);
  };

// Inside AuthProvider in AuthContext.jsx
useEffect(() => {
  const storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");

  if (!storedToken) {
    clearAuth();
    setLoading(false);
    return;
  }

  try {
    const payload = JSON.parse(atob(storedToken.split('.')[1]));
    const expiryTime = payload.exp * 1000;

    if (Date.now() > expiryTime) {
      clearAuth();
      setLoading(false);
      return;
    }

    // Token is valid → now hydrate cached data
    setToken(storedToken);
    const storedEmail = localStorage.getItem("email") || sessionStorage.getItem("email");
    const storedIsAdmin = localStorage.getItem("isAdmin") || sessionStorage.getItem("isAdmin");
    const storedRoleId = localStorage.getItem("roleId") || sessionStorage.getItem("roleId") || "customer";
    const storedPermissions = localStorage.getItem("permissions") || sessionStorage.getItem("permissions");
    setemail(storedEmail);
    setIsAdmin(storedIsAdmin === "true");
    setRoleId(storedRoleId);

    // Migrate sessions created before auth data became cross-tab persistent.
    if (storedEmail) localStorage.setItem("email", storedEmail);
    if (storedIsAdmin) localStorage.setItem("isAdmin", storedIsAdmin);
    localStorage.setItem("roleId", storedRoleId);
    if (storedPermissions) localStorage.setItem("permissions", storedPermissions);

    if (storedPermissions) {
      try { setPermissions(JSON.parse(storedPermissions)); } catch { setPermissions([]); }
    }

    const storedProfile = localStorage.getItem("Profile");
    if (storedProfile) {
      setProfile(JSON.parse(storedProfile));
    }

  } catch {
    clearAuth();
  } finally {
    setLoading(false);
  }
}, []);



  // ✅ Centralized register
  // AuthContext.jsx
  const registerUser = async (email, password) => {
    try {
      const res = await fetch(`${apiAuthUrl}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
  
      const data = await res.json();
      if (!res.ok || data.error) {
        showToast(`${data.error || "Registration failed"}`, "danger"); 
        return { success:false, message: "Registration failed!" };
      }
  
      // Optionally navigate user after success
      navigate('/login');
      showToast("Check your email to confirm your account!")
      return { success: true, message: "Check your email to confirm your account!" };
    } catch (err) {
      showToast(err.message, "danger")
      return { success: false, error: err.message };
    }
  };  

  // ✅ Centralized forgot password
  const resetPasswordRequest = async (email) => {
    try {
      const res = await fetch(`${apiAuthUrl}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) showToast(`${data.error || "Reset request failed"}`, "danger");
      
      showToast("Please check your email");
      return { success: true, data };
    } catch (err) {
      showToast(`${err.message || "Reset request failed"}`, "danger");
      return { success: false, error: err.message };
    }
  };

  // ✅ Centralized update password
  const updatePassword = async (token, newPassword) => {
    try {
      const res = await fetch(`${apiAuthUrl}/update-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Password update failed");
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

// ✅ Centralized login function
const loginUser = async (email, password) => {
  try {
    const res = await fetch(`${apiAuthUrl}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();
    
    if (!res.ok || data.error) throw new Error(data.error || "Login failed");

    const { token: newToken, email: userEmail, isAdmin, roleId: newRoleId, permissions: newPerms, profile: newProfile } = data;
    
    saveToken(newToken);
    
    localStorage.setItem("email", userEmail);
    localStorage.setItem("isAdmin", String(isAdmin));
    localStorage.setItem("roleId", newRoleId || "customer");
    localStorage.setItem("permissions", JSON.stringify(newPerms || []));
    localStorage.setItem("Profile", JSON.stringify(newProfile)); 

    setemail(userEmail);
    setIsAdmin(isAdmin);
    setRoleId(newRoleId || "customer");
    setPermissions(newPerms || []);
    setProfile(newProfile);
    // Navigate to the dashboard or home page
    navigate('/'); 

    return { success: true, data };
  } catch (err) {
    // Clear token/session data on failure
    setToken("");
    setemail(null);
    ["token", "email", "isAdmin", "roleId", "permissions"].forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    return { success: false, error: err.message || "An unexpected error occurred." };
  }
};

const clearAuth = () => {
  setToken("");
  setemail(null);
  setProfile(null);
  setIsAdmin(false);
  setRoleId("customer");
  setPermissions([]);
  ["token", "email", "isAdmin", "roleId", "permissions"].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  localStorage.removeItem("Profile");
};

const hasPermission = useCallback(
  (permission) => checkPermission(permissions, permission),
  [permissions]
);

const logout = async () => {
  const activeToken = token || localStorage.getItem("token") || sessionStorage.getItem("token");

  try {
    const response = await fetch(`${apiAuthUrl}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(activeToken ? { Authorization: `Bearer ${activeToken}` } : {}),
      },
    });

    if (!response.ok && response.status !== 401) {
      throw new Error('Logout failed');
    }
  } catch (e) {
    showToast("Logout API failed, clearing locally", "warning");
  } finally {
    clearAuth();
    showToast("Logout Done.", "warning")
    navigate('/login');
  }
};

  return (
    <AuthContext.Provider
    value={{
      email,
      loading,
      setemail,
      isAdmin,
      setIsAdmin,
      roleId,
      permissions,
      hasPermission,
      canAccessAdmin: () => canAccessAdmin(permissions, isAdmin, roleId),
      token,
      loginUser,
      saveToken,
      setProfile,
      profile,
      registerUser,
      resetPasswordRequest,
      updatePassword,
      logout,
    }}
  >
    {children}
  </AuthContext.Provider>
  
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
